import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Drawer,
  List,
  Typography,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  useMediaQuery,
  useTheme as useMuiTheme,
  SwipeableDrawer,
  Tooltip,
  Badge,
  Chip,
  alpha,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  SupportAgent as SupportAgentIcon,
  Settings as SettingsIcon,
  Business as BusinessIcon,
  Assignment as AssignmentIcon,
  AccountBalance as AccountBalanceIcon,
  ExpandMore as ExpandMoreIcon,
  Edit as EditIcon,
  Event as EventIcon,
  Casino as CasinoIcon,
  Monitor as MonitorIcon,
  WhatsApp as WhatsAppIcon,
  DirectionsCar as DirectionsCarIcon,
  EmojiEvents as EmojiEventsIcon,
} from '@mui/icons-material';

import logo from '../assets/logo.png';
import logoWhite from '../assets/logo-white.png';
import logo192 from '../assets/logo192.png';
import logo192White from '../assets/logo192-white.png';
import fundoMenuAlvo from '../assets/fundo_menu_alvo.png';
import fundoMenuBola from '../assets/fundo_menu_bola.png';
import fundoMenuRosa from '../assets/fundo_menu_rosa.png';
import fundoMenuAzulEscuro from '../assets/fundo_menu_azul_escuro.png';
import { useAuth, usePermissions } from '../contexts/AuthContext';
import { RELATORIOS_PAGE_PERMISSIONS } from '../pages/relatorios';
import { useTheme as useAppTheme } from '../contexts/ThemeContext';
import { useAtendimentoNotification } from '../contexts/AtendimentoNotificationContext';
import {
  copaSidebarBrandSubtitleSx,
  copaSidebarBrandTitleSx,
  copaSidebarTrophyBoxSx,
} from '../styles/customThemes/copa';
import { UserAvatar } from '../components/common';
import {
  SIDEBAR_WIDTH,
  SIDEBAR_WIDTH_COLLAPSED,
  capitalizeRole,
  formatMenuLabel,
  getSidebarBackgroundDecorSx,
  getSidebarBrandSx,
  getSidebarFadeGradient,
  getSidebarIconWrapSx,
  getSidebarMenuButtonSx,
  getSidebarPaperSx,
  getSidebarRoleChipSx,
  getSidebarScrollSx,
  getSidebarSubmenuContainerSx,
  getSidebarUserCardSx,
  getSidebarStickyBg,
  getMobileDrawerBackdropSx,
} from './sidebarStyles';

// Larguras do drawer (exportadas para Layout/Header)
const drawerWidth = SIDEBAR_WIDTH;
const drawerWidthCollapsed = SIDEBAR_WIDTH_COLLAPSED;

interface MenuItem {
  text: string;
  icon?: React.ReactElement;
  path?: string;
  permission?: string | string[];
  children?: MenuItem[];
}

const menuItems: MenuItem[] = [
  {
    text: 'Dashboard',
    icon: <DashboardIcon />,
    path: '/dashboard',
  },
  {
    text: 'Atendimento',
    icon: <WhatsAppIcon sx={{ color: '#25d366' }} />,
    permission: 'atendimento.acessar',
    children: [
      {
        text: '- Dashboard',
        path: '/atendimento/dashboard',
        permission: 'atendimento.dashboard.acessar',
      },
      {
        text: '- Atendimentos',
        path: '/atendimento/atendimentos',
        permission: 'atendimento.tickets.acessar',
      },
      {
        text: '- Contatos',
        path: '/atendimento/contatos',
        permission: 'atendimento.contatos.acessar',
      },
      {
        text: '- Tags',
        path: '/atendimento/tags',
        permission: 'atendimento.tags.acessar',
      },
      {
        text: '- Mensagens Rápidas',
        path: '/atendimento/mensagens-rapidas',
        permission: 'atendimento.mensagens_rapidas.acessar',
      },
      {
        text: '- Figurinhas',
        path: '/atendimento/figurinhas',
        permission: 'atendimento.figurinhas.acessar',
      },
      {
        text: '- Ranking',
        path: '/atendimento/ranking',
        permission: 'atendimento.ranking.acessar',
      },
    ],
  },
  {
    text: 'Clientes',
    icon: <PeopleIcon />,
    permission: 'clientes.acessar',
    children: [
      {
        text: '- Lista de Clientes',
        path: '/clients',
        permission: 'clientes.acessar',
      },
      {
        text: '- Pré-Cadastros',
        path: '/clients/pre-cadastros',
        permission: 'clientes.acessar',
      },
    ],
  },
  {
    text: 'Chamados',
    icon: <SupportAgentIcon />,
    path: '/chamados',
    permission: 'chamados.acessar',
  },
  {
    text: 'Administração',
    icon: <AssignmentIcon />,
    permission: 'administracao.acessar',
    children: [
      {
        text: '- Contratos',
        path: '/contracts',
        permission: 'contratos.acessar',
      },
      {
        text: '- Escritórios',
        path: '/escritorios',
        permission: 'administracao.escritorios.acessar',
      },
      {
        text: '- Equipamentos',
        path: '/equipments',
        permission: 'administracao.equipamentos.acessar',
      },
      {
        text: '- Fornecedores',
        path: '/fornecedores',
        permission: 'financeiro.fornecedores.acessar',
      },
      {
        text: '- FTTH',
        path: '/ftth',
        permission: 'administracao.ftth.acessar',
      },
      {
        text: '- Funcionários',
        path: '/settings/funcionarios',
        permission: 'administracao.funcionarios.acessar',
      },
      {
        text: '- Planos',
        path: '/plans',
        permission: 'administracao.planos.acessar',
      },
      {
        text: '- Propostas',
        path: '/propostas',
        permission: 'administracao.propostas.acessar',
      },
      {
        text: '- Serviços',
        path: '/services',
        permission: 'contratos.acessar',
      },
      {
        text: '- Usuários',
        path: '/settings/users',
        permission: 'administracao.usuarios.acessar',
      },
      {
        text: '- Grupos de Permissões',
        path: '/settings/grupos',
        permission: 'administracao.grupos.acessar',
      }
    ],
  },
  {
    text: 'Tarefas',
    icon: <EventIcon />,
    path: '/tarefas',
  },
  {
    text: 'Financeiro',
    icon: <AccountBalanceIcon />,
    permission: 'financeiro.acessar',
    children: [
      {
        text: '- Caixa Financeiro',
        path: '/caixa-financeiro',
        permission: 'financeiro.caixa.acessar',
      },
      {
        text: '- Comissões',
        path: '/comissoes',
        permission: 'financeiro.comissoes.acessar',
      },
      {
        text: '- Contas a Pagar',
        path: '/contas-a-pagar',
        permission: 'financeiro.contas_pagar.acessar',
      },
      {
        text: '- Créditos',
        path: '/creditos',
        permission: 'financeiro.creditos.acessar',
      },
      {
        text: '- Faturas e Cobranças',
        path: '/faturas-cobrancas',
        permission: 'financeiro.acessar',
      },
      {
        text: '- Notas NFCom',
        path: '/notas-nfcom',
        permission: 'financeiro.acessar',
      }
    ],
  },
  {
    text: 'Relatórios',
    icon: <AssignmentIcon />,
    path: '/reports',
    permission: RELATORIOS_PAGE_PERMISSIONS,
  },
  {
    text: 'Sorteador',
    icon: <CasinoIcon />,
    path: '/sorteador',
  },
  {
    text: 'Monitoramento',
    icon: <MonitorIcon />,
    children: [
      {
        text: '- Clientes',
        path: '/monitoramento/clientes',
      },
      {
        text: '- Ctos',
        path: '/monitoramento/ctos',
      },
      {
        text: '- Técnicos',
        path: '/monitoramento/tecnicos',
      },
      {
        text: '- Tráfego',
        path: '/monitoramento/trafego',
      },
      {
        text: '- Rede',
        path: '/monitoramento/rede',
      }
    ],
  },
  {
    text: 'Frotas',
    icon: <DirectionsCarIcon />,
    permission: 'frota.acessar',
    children: [
      {
        text: '- Veículos',
        path: '/frota/veiculos',
        permission: 'frota.veiculos.acessar',
      },
      {
        text: '- Abastecimentos',
        path: '/frota/abastecimentos',
        permission: 'frota.abastecimentos.acessar',
      },
      {
        text: '- Manutenções',
        path: '/frota/manutencoes',
        permission: 'frota.manutencoes.acessar',
      },
      {
        text: '- Trocas de Óleo',
        path: '/frota/trocas-oleo',
        permission: 'frota.trocas_oleo.acessar',
      },
    ],
  },
  {
    text: 'Configurações',
    icon: <SettingsIcon />,
    permission: 'configuracoes.acessar',
    children: [
      {
        text: '- Backup',
        path: '/settings/backup',
        permission: 'configuracoes.backup.acessar',
      },
      {
        text: '- Atualização',
        path: '/settings/atualizacao',
        permission: 'configuracoes.atualizacao.acessar',
      },
      {
        text: '- CGNAT',
        path: '/cgnat',
        permission: 'configuracoes.cgnat.acessar',
      },
      {
        text: '- Concentradores NAS',
        path: '/settings/nas',
        permission: 'configuracoes.concentradores.acessar',
      },
      {
        text: '- Feriados',
        path: '/settings/holidays',
        permission: 'configuracoes.feriados.acessar',
      },
      {
        text: '- Gateways de Cobrança',
        path: '/settings/gateways',
        permission: 'configuracoes.gateways_cobranca.acessar',
      },
      {
        text: '- Gateways SMS',
        path: '/settings/gateways-sms',
        permission: 'configuracoes.gateways_sms.acessar',
      },
      {
        text: '- Gateways WhatsApp',
        path: '/settings/gateways-whatsapp',
        permission: 'configuracoes.gateways_whatsapp.acessar',
      },
      {
        text: '- Gateways NFCom',
        path: '/settings/gateways-nfcom',
        permission: 'configuracoes.gateways_nfcom.acessar',
      },
      {
        text: '- Importar Dados',
        path: '/settings/import-data',
        permission: 'configuracoes.importar_dados.acessar',
      },
      {
        text: '- Licença',
        path: '/settings/licenca',
        permission: 'configuracoes.parametros.acessar',
      },
      {
        text: '- Logs',
        path: '/settings/logs',
        permission: 'configuracoes.logs.acessar',
      },
      {
        text: '- Mensagens',
        path: '/mensagens',
        permission: 'configuracoes.mensagens.acessar',
      },
      {
        text: '- Parâmetros',
        path: '/settings/parameters',
        permission: 'configuracoes.parametros.acessar',
      },
      {
        text: '- Templates',
        path: '/settings/templates',
        permission: 'configuracoes.templates.acessar',
      }
    ],
  },
  {
    text: 'Outros',
    icon: <BusinessIcon />,
    children: [
      {
        text: 'Editor de Texto',
        icon: <EditIcon />,
        path: '/text-editor',
      },
      {
        text: 'Teste Abas',
        icon: <AssignmentIcon />,
        path: '/teste-abas',
      },
    ],
  },
];

interface SidebarProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  onHoverChange?: (isHovered: boolean) => void;
}

const Sidebar: React.FC<SidebarProps> = ({ open, setOpen, onHoverChange }) => {
  const theme = useMuiTheme();
  const { isDarkMode, themeId } = useAppTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { can } = usePermissions();
  const { badgeCount, temNovidade } = useAtendimentoNotification();
  const podeVerBadgeAtendimento = can('atendimento.tickets.acessar');

  const [expandedMenus, setExpandedMenus] = useState<Set<string>>(new Set());
  const [wasTemporarilyExpanded, setWasTemporarilyExpanded] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  const shouldShowExpanded = open || isHovered;

  // Limpar flag de expansão temporária quando o menu é fechado manualmente
  useEffect(() => {
    if (!open && wasTemporarilyExpanded) {
      setWasTemporarilyExpanded(false);
      // Limpar submenus expandidos
      setExpandedMenus(new Set());
    }
  }, [open, wasTemporarilyExpanded]);

  // Expandir automaticamente o menu pai da rota atual
  useEffect(() => {
    const parentMenu = menuItems.find((item) =>
      item.children?.some((child) => child.path === location.pathname),
    );

    if (parentMenu) {
      setExpandedMenus(new Set([parentMenu.text]));
    }
  }, [location.pathname]);

  const handleNavigate = (path: string) => {
    navigate(path);

    // Se foi expandido temporariamente, voltar ao estado colapsado
    if (wasTemporarilyExpanded) {
      setOpen(false);
      setWasTemporarilyExpanded(false);
    }

    // Fechar drawer em mobile/tablet após navegar
    if (isMobile || isTablet) {
      setOpen(false);
    }
  };

  const toggleMenuExpansion = (menuText: string, hasChildren: boolean) => {
    // Se tem filhos e o menu está colapsado, expandir temporariamente
    if (hasChildren && !open && !isMobile && !isTablet) {
      setOpen(true);
      setWasTemporarilyExpanded(true);
      setExpandedMenus(new Set([menuText]));
      return;
    }

    setExpandedMenus((prev) => {
      const isCurrentlyExpanded = prev.has(menuText);

      if (isCurrentlyExpanded) {
        if (wasTemporarilyExpanded) {
          setOpen(false);
          setWasTemporarilyExpanded(false);
        }
        return new Set<string>();
      }

      // Accordion: ao abrir um menu, fecha os demais
      return new Set([menuText]);
    });
  };

  const handleSidebarMouseEnter = () => {
    if (!isMobile && !isTablet && !open) {
      setIsHovered(true);
      onHoverChange?.(true);
    }
  };

  const handleSidebarMouseLeave = () => {
    setIsHovered(false);
    onHoverChange?.(false);
  };

  const isMenuItemVisible = (item: MenuItem) => {
    if (user?.perfil === 'administrador') return true;

    const permissionAllowed = !item.permission
      ? true
      : (Array.isArray(item.permission)
        ? item.permission.some((code) => user?.permissions?.includes(code))
        : user?.permissions?.includes(item.permission));

    return permissionAllowed;
  };

  const renderMenuIcon = (item: MenuItem) => {
    if (!item.icon) return null;

    if (podeVerBadgeAtendimento && temNovidade && item.text === 'Atendimento') {
      return (
        <Badge
          badgeContent="!"
          color="error"
          overlap="circular"
          sx={{
            '& .MuiBadge-badge': {
              fontSize: '0.7rem',
              height: 18,
              minWidth: 18,
              fontWeight: 800,
              padding: '0 4px',
            },
          }}
        >
          {item.icon}
        </Badge>
      );
    }

    return item.icon;
  };

  const renderMenuPrimary = (item: MenuItem) => {
    const isAtendimentosSubmenu = item.path === '/atendimento/atendimentos';
    const mostrarNumero = podeVerBadgeAtendimento && temNovidade && isAtendimentosSubmenu && badgeCount > 0;

    if (!mostrarNumero) {
      return formatMenuLabel(item.text);
    }

    return (
      <Box component="span" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', gap: 1 }}>
        <span>{formatMenuLabel(item.text)}</span>
        <Box
          component="span"
          sx={{
            bgcolor: 'error.main',
            color: '#fff',
            borderRadius: '10px',
            minWidth: 20,
            height: 20,
            px: 0.75,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '0.7rem',
            fontWeight: 700,
            lineHeight: 1,
            flexShrink: 0,
          }}
        >
          {badgeCount > 99 ? '99+' : badgeCount}
        </Box>
      </Box>
    );
  };

  const renderMenuItem = (item: MenuItem, level: number = 0) => {
    const hasChildren = item.children && item.children.length > 0;
    const isExpanded = expandedMenus.has(item.text);
    const isChildSelected = Boolean(
      hasChildren && item.children?.some((child) => location.pathname === child.path),
    );
    const isActive = location.pathname === item.path;
    const isParentOfActive = Boolean(hasChildren && isChildSelected && level === 0);
    const isHighlighted = isActive || isParentOfActive;
    const isSubmenu = level > 0;

    return (
      <React.Fragment key={`${item.text}-${level}`}>
        <ListItem disablePadding component="div" sx={{ display: 'block' }}>
          <Tooltip
            title={formatMenuLabel(item.text)}
            placement="right"
            arrow
            disableHoverListener={shouldShowExpanded || isMobile || (hasChildren && !shouldShowExpanded)}
          >
            <ListItemButton
              component="div"
              onClick={
                hasChildren
                  ? () => toggleMenuExpansion(item.text, hasChildren)
                  : () => handleNavigate(item.path!)
              }
              selected={isHighlighted}
              sx={getSidebarMenuButtonSx(theme, {
                selected: isHighlighted,
                parentActive: isParentOfActive,
                level,
                expanded: shouldShowExpanded,
                isMobile,
                hasChildren: Boolean(hasChildren),
                themeId,
              })}
            >
              {!isSubmenu && item.icon && (
                <ListItemIcon
                  sx={{
                    ...getSidebarIconWrapSx(theme, isHighlighted),
                    mr: shouldShowExpanded ? 1.5 : 'auto',
                  }}
                >
                  {renderMenuIcon(item)}
                </ListItemIcon>
              )}
              <ListItemText
                primary={renderMenuPrimary(item)}
                sx={{
                  opacity: shouldShowExpanded ? 1 : 0,
                  width: shouldShowExpanded ? 'auto' : 0,
                  ml: isSubmenu && shouldShowExpanded ? 0 : !item.icon && shouldShowExpanded ? 2 : 0,
                  my: 0,
                  '& .MuiTypography-root': {
                    fontSize: isSubmenu ? '0.8125rem' : '0.875rem',
                    fontWeight: isHighlighted ? 600 : 400,
                    color: 'inherit',
                  },
                  whiteSpace: 'nowrap',
                  transition: theme.transitions.create(['opacity', 'width'], {
                    duration: theme.transitions.duration.shorter,
                  }),
                }}
              />
              {hasChildren && shouldShowExpanded && (
                <ExpandMoreIcon
                  sx={{
                    transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: theme.transitions.create('transform', {
                      duration: theme.transitions.duration.shorter,
                    }),
                    fontSize: '1.2rem',
                    opacity: 0.9,
                    flexShrink: 0,
                  }}
                />
              )}
            </ListItemButton>
          </Tooltip>
        </ListItem>

        {hasChildren && shouldShowExpanded && (
          <Collapse in={isExpanded} timeout={{ enter: 280, exit: 220 }} unmountOnExit>
            <List component="div" disablePadding sx={getSidebarSubmenuContainerSx()}>
              {item.children!.filter((child) => isMenuItemVisible(child)).map((child) =>
                renderMenuItem(child, level + 1),
              )}
            </List>
          </Collapse>
        )}
      </React.Fragment>
    );
  };

  const stickyBg = getSidebarStickyBg(themeId, theme, isDarkMode);
  const sidebarDecorImage =
    themeId === 'copa'
      ? fundoMenuBola
      : themeId === 'rosa'
        ? fundoMenuRosa
        : themeId === 'usa'
          ? fundoMenuAzulEscuro
          : fundoMenuAlvo;

  const drawer = (
    <Box
      onMouseEnter={handleSidebarMouseEnter}
      onMouseLeave={handleSidebarMouseLeave}
      sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden', position: 'relative' }}
    >
      <Box aria-hidden sx={getSidebarBackgroundDecorSx(theme, themeId, shouldShowExpanded)}>
        <Box component="img" src={sidebarDecorImage} alt="" />
      </Box>

      <List component="div" className="sidebar-menu-list" sx={getSidebarScrollSx(theme, themeId)}>
        <Box component="div" sx={{ position: 'sticky', top: 0, zIndex: 2, flexShrink: 0, mb: 0.5 }}>
          <Box sx={{ bgcolor: stickyBg }}>
            {!isMobile && (
              <Box sx={getSidebarBrandSx(theme, shouldShowExpanded, themeId)}>
                {themeId === 'copa' ? (
                  <>
                    <Box sx={copaSidebarTrophyBoxSx}>
                      <EmojiEventsIcon />
                    </Box>
                    {shouldShowExpanded && (
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={copaSidebarBrandTitleSx}>Copa do Mundo</Typography>
                        <Typography sx={copaSidebarBrandSubtitleSx}>Brasil 2026</Typography>
                      </Box>
                    )}
                  </>
                ) : (
                  <Box
                    component="img"
                    src={
                      shouldShowExpanded
                        ? themeId === 'dark'
                          ? logoWhite
                          : logo
                        : themeId === 'dark'
                          ? logo192White
                          : logo192
                    }
                    alt="SysUP"
                    sx={{
                      height: shouldShowExpanded ? 52 : 48,
                      width: shouldShowExpanded ? 'auto' : 48,
                      maxWidth: shouldShowExpanded ? '100%' : 48,
                      objectFit: 'contain',
                      flexShrink: 0,
                      transition: theme.transitions.create(['width', 'height'], {
                        duration: theme.transitions.duration.shorter,
                      }),
                    }}
                  />
                )}
              </Box>
            )}

            {user && (
              <Box sx={{ ...getSidebarUserCardSx(theme, shouldShowExpanded, themeId), pt: 0.5 }}>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: shouldShowExpanded ? 1.5 : 0,
                    justifyContent: 'center',
                    width: '100%',
                  }}
                >
                  <UserAvatar
                    size={shouldShowExpanded ? 50 : 40}
                    showUploadButton={false}
                    editable={false}
                    sx={{
                      border: shouldShowExpanded
                        ? `2px solid ${themeId === 'copa'
                          ? alpha('#FFDF00', 0.55)
                          : isDarkMode
                            ? alpha('#3b82f6', 0.4)
                            : alpha(theme.palette.primary.main, 0.35)
                        }`
                        : 'none',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
                      borderRadius: '50%',
                      flexShrink: 0,
                      transition: theme.transitions.create('width', { duration: 200 }),
                    }}
                  />
                  {shouldShowExpanded && (
                    <Box sx={{ minWidth: 0, textAlign: 'left' }}>
                      <Typography
                        variant="subtitle2"
                        noWrap
                        fontWeight={600}
                        sx={{
                          fontSize: '0.9rem',
                          color: themeId === 'copa' ? '#ffffff' : undefined,
                        }}
                      >
                        {user.nome.split(' ')[0]}
                      </Typography>
                      <Chip
                        label={capitalizeRole(user.perfil)}
                        size="small"
                        sx={{ ...getSidebarRoleChipSx(theme, themeId), mt: 0.1 }}
                      />
                    </Box>
                  )}
                </Box>
              </Box>
            )}
          </Box>

        </Box>

        <Box sx={{ px: 0.25, pb: 1 }}>
          {menuItems.filter(isMenuItemVisible).map((item) => renderMenuItem(item))}
        </Box>
      </List>
    </Box>
  );

  const commonDrawerProps = {
    ModalProps: { keepMounted: true },
    sx: {
      '& .MuiDrawer-paper': getSidebarPaperSx(theme, shouldShowExpanded, isMobile, themeId),
      ...(isMobile && {
        '& .MuiBackdrop-root': getMobileDrawerBackdropSx(),
      }),
    },
  };

  const mobileDrawerProps = {
    ...commonDrawerProps,
    open,
    onClose: () => setOpen(false),
    onOpen: () => setOpen(true),
    disableSwipeToOpen: false,
    disableDiscovery: false,
    disableBackdropTransition: false,
  };

  const desktopDrawerProps = {
    ...commonDrawerProps,
    variant: 'permanent' as const,
    open: true,
  };

  return (
    <Box
      component="nav"
      sx={{
        width: {
          xs: 0, // Mobile: width 0 (drawer overlay)
          sm: isMobile ? 0 : (shouldShowExpanded ? drawerWidth : drawerWidthCollapsed)
        },
        flexShrink: { sm: 0 }
      }}
    >
      {isMobile ? (
        <SwipeableDrawer {...mobileDrawerProps}>
          {drawer}
        </SwipeableDrawer>
      ) : (
        <Drawer {...desktopDrawerProps}>
          {drawer}
        </Drawer>
      )}
    </Box>
  );
};

export default Sidebar; 
